import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useLocation} from 'react-router-dom';
import {Toast} from 'antd-mobile';
import dayjs from 'dayjs';
import styles from './index.module.scss';
import NFTHeader from '@/components/NFTHeader';
import ListView from '@/components/AntListView';
import {composeRecord} from '@/api/projectManager_self';
import {hideLoading, showLoading} from '@/components/loading';
import {USE_MOCK, MOCK_RECORDS} from '../mock';
import {getActivityCodeFromQuery, parseExternalQuery, useStatusBarInset} from '../utils';

/**
 * 合成记录（新页面）
 * 路由：/collectionSynthesis/record
 * 入参：?ac=活动码（与合成页一致）
 */
const Record = () => {
  const location = useLocation();
  const [list, setList] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [isLoad, setIsLoad] = useState(false);
  const currentPage = useRef(1);
  const pageSize = useRef(10);
  const loadingRef = useRef(false);
  const statusBarInset = useStatusBarInset();

  const query = useMemo(
    () => parseExternalQuery(location.search),
    [location.search]
  );
  const activityCode = getActivityCodeFromQuery(query);

  const formatTime = (value) => {
    if (!value) return '-';
    const parsed = dayjs(value);
    if (parsed.isValid()) return parsed.format('YYYY.MM.DD HH:mm:ss');
    return String(value).replace(/-/g, '.');
  };

  const formatNftNo = (value) => {
    if (!value) return '-';
    return String(value)
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean)
      .map((item) => (item.startsWith('#') ? item : `#${item}`))
      .join(' ');
  };

  const loadData = useCallback(async (isRefresh = true) => {
    if (loadingRef.current) return;
    loadingRef.current = true;
    try {
      if (!activityCode && USE_MOCK) {
        setList(MOCK_RECORDS);
        setHasMore(false);
        setIsLoad(true);
        return;
      }
      if (isRefresh) {
        currentPage.current = 1;
        showLoading();
      } else {
        currentPage.current += 1;
      }
      const res = await composeRecord({
        currentPage: currentPage.current,
        pageSize: pageSize.current,
        activityCode,
      });
      if (res.code !== '000000') throw res.msg;
      const pageList = res.data?.list || [];
      const totalCount = res.data?.totalCount ?? pageList[0]?.totalCount;
      setList((prev) => (isRefresh ? pageList : [...prev, ...pageList]));
      setHasMore(
        totalCount != null
          ? currentPage.current * pageSize.current < Number(totalCount)
          : pageList.length >= pageSize.current
      );
      setIsLoad(true);
    } catch (error) {
      Toast.show(error);
      if (!isRefresh) currentPage.current = Math.max(1, currentPage.current - 1);
      setIsLoad(true);
      setHasMore(false);
    } finally {
      hideLoading();
      loadingRef.current = false;
    }
  }, [activityCode]);

  useEffect(() => {
    loadData(true);
  }, [loadData]);

  return (
    <div className={styles.page} style={{'--sat': `${statusBarInset}px`}}>
      <NFTHeader midText="合成记录" bgColor="#FFFFFF" />
      <div className={styles.list}>
        <ListView
          dataSource={list}
          hasMore={hasMore}
          isLoad={isLoad}
          emtyDesc="暂无合成记录"
          renderItem={(item, index) => (
            <div className={styles.item} key={item.upgradeId || item.nftNo || index}>
              <div className={styles.top}>
                <img
                  className={styles.cover}
                  src={item.upgradeImg || item.nftImage || item.nftImageUrl || item.imageBg}
                  alt=""
                />
                <div className={styles.info}>
                  <p className={styles.name}>{item.afterName || item.collectionName || item.nftName || item.name || '-'}</p>
                  <p className={styles.no}>{formatNftNo(item.afterNftNo || item.nftNo)}</p>
                </div>
              </div>
              <div className={styles.bottom}>
                <p className={styles.time}>
                  合成时间：{formatTime(item.upgradeTime || item.upgradedTime || item.createTime || item.composeTime || item.gmtCreate)}
                </p>
              </div>
            </div>
          )}
          onRefresh={async () => {
            await loadData(true);
          }}
          onEndReached={async () => {
            await loadData(false);
          }}
        />
      </div>
    </div>
  );
};

export default Record;
